// Storage upload with a real 0–100 progress readout.
//
// supabase-js v2's storage.upload() returns a promise with no progress
// callback, and the brief requires a live percentage on the card. So this
// posts to the same Storage REST endpoint the SDK targets, via XMLHttpRequest,
// which does expose upload.onprogress.
//
// It does NOT create a second Supabase client — it reuses the singleton for
// the auth token and only borrows the project URL/key that the singleton was
// built from. Everything else in this feature goes through the SDK.

import { supabase } from '../../supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Keep object keys predictable: strip accents, collapse anything unusual.
export const sanitizeFileName = (name) =>
  (name || 'archivo')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9.\-_]/g, '-')
    .replace(/-+/g, '-')
    .slice(-80);

export const uploadWithProgress = async ({ bucket, path, file, onProgress }) => {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Supabase no está configurado.');
  }

  // If a session ever exists (post-expo cliente auth), prefer its token.
  let token = SUPABASE_ANON_KEY;
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) token = data.session.access_token;
  } catch {
    // Anonymous is the expected case for now.
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`, true);
    xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('x-upsert', 'false');
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve(`${bucket}/${path}`);
      } else {
        let message = `Error ${xhr.status}`;
        try {
          message = JSON.parse(xhr.responseText)?.message || message;
        } catch {
          /* keep the status-code message */
        }
        reject(new Error(message));
      }
    };

    xhr.onerror = () => reject(new Error('Fallo de red durante la subida.'));
    xhr.send(file);
  });
};
